import { FC, useCallback, useContext, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  InfoBlock,
  ISelectOption,
  Pagination,
  Preloader,
  Select,
} from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import { ClientSuggest } from 'components';
import { FeatureHOC, useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import {
  ClientModal,
  DepositModal,
  NotPermissionModal,
  ResetPasswordModal,
} from 'modals';
import { SettingsContext } from 'settings';

import { IUser } from '../../types';
import s from './ClubReviews.module.scss';
import {
  EReviewsSort,
  IClubReviewsRequest,
  IClubReviewsResponse,
  IReadClubReviewRequest,
  IReadClubReviewResponse,
  IReviewsCountResponse,
  IReadAllClubReviewsResponse,
} from './interfaces';
import { Review } from './Review';
import { ReviewModal } from './ReviewModal';
import {
  CLUB_REVIEWS,
  IS_READ_FRAGMENT,
  READ_CLUB_REVIEW,
  REVIEWS_COUNT,
  READ_ALL_CLUB_REVIEWS,
} from './schema';
import { Summary } from './Summary';

export const ClubReviews: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const [hasReviews, setHasReviews] = useState(false);
  const [variables, setVariables] = useState<IClubReviewsRequest>({
    page: 1,
    input: {
      sort: EReviewsSort.NEW_FIRST,
      user_uuid: null,
    },
  });
  const [fullReviewId, setFullReviewId] = useState<number | null>(null);
  const isReviewsAvailable = useIsFeatureAvailable('club_review');
  const {
    state: {
      settings: { feedbackEnabled },
    },
  } = useContext(SettingsContext);
  const [clientUUID, setClientUUID] = useState<string | null>(null);

  const SORT_OPTIONS: Array<ISelectOption<EReviewsSort>> = [
    { value: EReviewsSort.NEW_FIRST, title: t('reviews.filter.first.new') },
    { value: EReviewsSort.OLD_FIRST, title: t('reviews.filter.first.old') },
    {
      value: EReviewsSort.HIGH_GRADE_FIRST,
      title: t('reviews.filter.first.good'),
    },
    {
      value: EReviewsSort.LOW_GRADE_FIRST,
      title: t('reviews.filter.first.bad'),
    },
  ];

  const { data, loading } = useQuery<IClubReviewsResponse, IClubReviewsRequest>(
    CLUB_REVIEWS,
    {
      fetchPolicy: 'cache-and-network',
      skip: !isReviewsAvailable,
      variables,
      onCompleted: (response) => {
        if (!response) return;

        if (!hasReviews && response.clubComments.summary.average_grade) {
          setHasReviews(true);
        }
      },
    }
  );

  const [readAllClubReviewsMutation, { loading: isAllReviewsReading }] =
    useMutation<IReadAllClubReviewsResponse>(READ_ALL_CLUB_REVIEWS, {
      refetchQueries: [CLUB_REVIEWS],
      awaitRefetchQueries: true,
      update: (cache, { data }) => {
        const isAllReviewsRead = data?.readAllClubComments;

        if (!isAllReviewsRead) return;

        cache.updateQuery<IReviewsCountResponse>(
          {
            query: REVIEWS_COUNT,
          },
          () => {
            return {
              clubComments: {
                summary: {
                  unread_total: 0,
                },
              },
            };
          }
        );
      },
      optimisticResponse: () => ({
        readAllClubComments: true,
      }),
    });
  const [readClubReviewMutation] = useMutation<
    IReadClubReviewResponse,
    IReadClubReviewRequest
  >(READ_CLUB_REVIEW, {
    update: (cache, { data }) => {
      if (!data) return;

      const newReviewData = data.readClubComment;

      cache.writeFragment({
        id: cache.identify(newReviewData),
        data: newReviewData,
        fragment: IS_READ_FRAGMENT,
      });

      cache.updateQuery<IReviewsCountResponse>(
        {
          query: REVIEWS_COUNT,
        },
        (cachedData) => {
          if (!cachedData) return cachedData;

          const { clubComments } = cachedData;
          let unread_total = clubComments.summary.unread_total;

          if (newReviewData.is_read) {
            unread_total -= 1;
          }

          return {
            clubComments: {
              summary: {
                unread_total: unread_total,
              },
            },
          };
        }
      );
    },
    optimisticResponse: (vars) => ({
      readClubComment: {
        id: vars.id,
        is_read: true,
        __typename: 'ClubComment',
      },
    }),
  });
  const readClubReview = useCallback(
    (id: number): void => {
      readClubReviewMutation({ variables: { id } });
    },
    [readClubReviewMutation]
  );

  const fullReviewData = useMemo(
    () => data?.clubComments.data.find((review) => review.id === fullReviewId),
    [fullReviewId, data?.clubComments.data]
  );

  const paginationInfo = data?.clubComments.paginatorInfo;
  const [notPermissionModalOpen, setNotPermissionModalOpen] =
    useState<boolean>(false);

  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [defaultClient, setIsDefaultClient] = useState<string | undefined>('');
  const [isResetPasswordModal, setIsResetPasswordModal] = useState<
    boolean | undefined
  >(false);
  const [currentClient, setCurrentClient] = useState<IUser | undefined>(
    undefined
  );

  return (
    <FeatureHOC alias="club_review" name={t('reviews')} strategy="replace">
      <Preloader
        className={s.preloader}
        loading={loading && !hasReviews}
        hasData={hasReviews || feedbackEnabled}
        noDataPlaceholder={t('reviews.disabled.placeholder')}
      >
        {!feedbackEnabled && (
          <InfoBlock
            iconName="InfoIcon"
            direction="row"
            color="blue"
            title={t('reviews.disabled.info')}
            message={t('reviews.disabled.info.message')}
            className={s.info}
          />
        )}
        <div className={s.page}>
          {notPermissionModalOpen && (
            <NotPermissionModal
              onClose={() => setNotPermissionModalOpen(false)}
            />
          )}
          {fullReviewData && (
            <ReviewModal
              data={fullReviewData}
              close={() => setFullReviewId(null)}
            />
          )}
          {isDepositOpen && (
            <DepositModal
              defaultClientUUID={defaultClient}
              onClose={() => {
                setIsDepositOpen(false);
                setClientUUID(null);
              }}
            />
          )}
          {clientUUID && !isDepositOpen && !isResetPasswordModal && (
            <ClientModal
              clientUUID={clientUUID}
              onClose={() => setClientUUID(null)}
              setIsDepositOpen={setIsDepositOpen}
              setIsDefaultClient={setIsDefaultClient}
              setIsResetPasswordModal={setIsResetPasswordModal}
              setCurrentClient={setCurrentClient}
            />
          )}
          {isResetPasswordModal && (
            <ResetPasswordModal
              currentClient={currentClient}
              onClose={() => setIsResetPasswordModal(false)}
              clientUUID={clientUUID}
            />
          )}
          <Summary data={data?.clubComments.summary} />
          <div className={s.content}>
            <div className={s.controls}>
              <ClientSuggest
                value={variables.input.user_uuid ?? null}
                placeholder={t('reviews.search.client')}
                onChange={(user_uuid) =>
                  setVariables((prevState) => ({
                    ...prevState,
                    input: { ...prevState.input, user_uuid },
                    page: 1,
                  }))
                }
                className={s.suggest}
                small
                disabled={
                  !permissions?.has('client_browse') &&
                  !permissions?.has('client_info')
                }
                reviews={true}
                withReplenishment
                setIsDefaultClient={setIsDefaultClient}
                setDepositOpen={setIsDepositOpen}
              />
              <div className={s.sortContainer}>
                <Select
                  name="reviews_sort"
                  options={SORT_OPTIONS}
                  value={variables.input.sort}
                  onChange={(sort) =>
                    setVariables((prevState) => ({
                      ...prevState,
                      input: { ...prevState.input, sort },
                      page: 1,
                    }))
                  }
                  disableClear
                  errorWrapperClassName={s.selectContainer}
                  className={s.select}
                />
                <Button
                  iconName="DoubleCheckIcon"
                  size="m"
                  variant="secondary"
                  className={s.btn}
                  isLoading={isAllReviewsReading}
                  onClick={() => readAllClubReviewsMutation()}
                >
                  {t('reviews.read.all')}
                </Button>
              </div>
            </div>
            <div className={s.reviews}>
              <Preloader
                loading={loading}
                hasData={Boolean(data?.clubComments.data.length)}
                noDataPlaceholder={t(
                  hasReviews
                    ? 'reviews.placeholder'
                    : 'reviews.placeholder.client'
                )}
              >
                {data?.clubComments.data.map((review) => (
                  <Review
                    key={`reviews-${review.id}`}
                    data={review}
                    readClubReview={readClubReview}
                    openClientModal={(clientUUID) => {
                      if (!permissions?.has('client_info')) {
                        setNotPermissionModalOpen(true);
                        return;
                      }

                      setClientUUID(clientUUID);
                    }}
                    onClick={(id) => {
                      setFullReviewId(id);
                      if (!review.is_read) {
                        readClubReview(id);
                      }
                    }}
                  />
                ))}
                <Pagination
                  onChange={(page) => {
                    if (page === paginationInfo?.currentPage) return;

                    setVariables((prevState) => ({
                      ...prevState,
                      page,
                    }));
                  }}
                  lastPage={paginationInfo?.lastPage ?? 1}
                  currentPage={paginationInfo?.currentPage ?? 1}
                />
              </Preloader>
            </div>
          </div>
        </div>
      </Preloader>
    </FeatureHOC>
  );
};
